import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { api } from '../App';
import toast from 'react-hot-toast';
import Pipeline from '../components/hiring/Pipeline';
import Jobs from '../components/hiring/Jobs';
import Interviews from '../components/hiring/Interviews';
import HiringAnalytics from '../components/hiring/HiringAnalytics';
import RecruitingAssistant from '../components/hiring/RecruitingAssistant';
import Branding from '../components/hiring/Branding';

const TABS = [
  ['pipeline', 'Pipeline', 'fa-diagram-project'],
  ['jobs', 'Jobs & Posting', 'fa-briefcase'],
  ['interviews', 'Interviews', 'fa-calendar-check'],
  ['analytics', 'Analytics', 'fa-chart-line'],
  ['assistant', 'AI Assistant', 'fa-robot'],
  ['branding', 'Careers Site', 'fa-palette'],
  ['cheatsheet', 'Closer Cheat Sheet', 'fa-book-open'],
];

function BetaStrip() {
  const [features, setFeatures] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get('/api/hiring/beta-status').then(r => setFeatures(r.data.features || [])).catch(() => {}); }, []);
  if (!features) return null;
  const liveCount = features.filter(f => f.status === 'live').length;
  return (
    <div className="bg-surface rounded-2xl border border-border" data-testid="beta-strip">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 text-left" data-testid="beta-strip-toggle">
        <span className="text-xs font-bold text-text-secondary">
          <i className="fa-solid fa-flask text-violet-500 mr-2" />Beta Feature Status
          <span className="ml-2 text-[10px] text-text-muted font-medium">{liveCount} live · {features.length - liveCount} in beta</span>
        </span>
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'} text-text-muted text-xs`} />
      </button>
      {open && (
        <div className="px-5 pb-4 grid sm:grid-cols-2 gap-2">
          {features.map(f => (
            <div key={f.key} className="bg-surface-alt rounded-xl p-3" data-testid={`beta-feature-${f.key}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-text-primary">{f.label}</p>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${f.status === 'live' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{f.status === 'live' ? 'Live' : 'Beta'}</span>
              </div>
              <p className="text-[10px] text-text-muted mt-1">{f.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HiringCenter() {
  const [tab, setTab] = useState('pipeline');
  const [sheet, setSheet] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('gcal') === 'connected') { toast.success('Google Calendar connected!'); setTab('interviews'); }
    if (p.get('gcal') === 'error') { toast.error('Google Calendar connection failed — try again'); setTab('interviews'); }
  }, []);

  const loadSheet = async (refresh = false) => {
    setSheetLoading(true);
    try {
      const r = await api.get(`/api/hiring/cheat-sheet${refresh ? '?refresh=true' : ''}`);
      setSheet(r.data);
    } catch { toast.error('Cheat sheet unavailable'); }
    setSheetLoading(false);
  };
  useEffect(() => { if (tab === 'cheatsheet' && !sheet) loadSheet(); }, [tab]); // eslint-disable-line

  return (
    <div className="space-y-6 animate-fade-in" data-testid="hiring-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-text-primary flex items-center gap-2">
          <i className="fa-solid fa-user-plus text-primary" />Hiring Center
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200" data-testid="hiring-beta-badge">Beta</span>
        </h1>
        <p className="text-sm text-text-muted mt-1">Post jobs everywhere, manage applicants through every stage, score with AI, and book interviews — all in one place.</p>
      </div>

      <BetaStrip />

      {/* Tabs */}
      <div className="flex flex-wrap border-b border-border">
        {TABS.map(([k, l, icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-all ${tab === k ? 'text-primary border-primary' : 'text-text-muted border-transparent hover:text-text-primary'}`}
            data-testid={`hiring-tab-${k}`}>
            <i className={`fa-solid ${icon} mr-1.5`} />{l}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && <Pipeline />}
      {tab === 'jobs' && <Jobs />}
      {tab === 'interviews' && <Interviews />}
      {tab === 'analytics' && <HiringAnalytics />}
      {tab === 'assistant' && <RecruitingAssistant />}
      {tab === 'branding' && <Branding />}

      {tab === 'cheatsheet' && (
        <div className="bg-surface rounded-2xl border border-border p-6" data-testid="cheat-sheet-panel">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-text-muted">{sheet?.generated_at ? `Generated ${new Date(sheet.generated_at).toLocaleString()}` : ''}</p>
            <div className="flex gap-2">
              <button onClick={() => loadSheet(true)} disabled={sheetLoading} className="btn-secondary text-xs" data-testid="regen-cheatsheet-btn">
                {sheetLoading ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5" /> : <i className="fa-solid fa-wand-magic-sparkles mr-1.5" />}Regenerate with AI
              </button>
              <button onClick={() => window.print()} className="btn-secondary text-xs" data-testid="print-cheatsheet-btn"><i className="fa-solid fa-print mr-1.5" />Print</button>
            </div>
          </div>
          {sheetLoading && !sheet ? (
            <div className="py-16 text-center text-text-muted text-sm"><i className="fa-solid fa-circle-notch fa-spin text-2xl block mb-3" />Writing the playbook…</div>
          ) : sheet ? (
            <div className="md-doc" data-testid="cheat-sheet-content"><ReactMarkdown>{sheet.content}</ReactMarkdown></div>
          ) : null}
        </div>
      )}
    </div>
  );
}
